import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Eye, EyeOff } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { DataVisualization } from "./DataVisualization";

// Helper to remove wrapping quotes from text
function removeWrappingQuotes(text) {
  if (!text || typeof text !== "string") return text;

  if (
    (text.startsWith('"') && text.endsWith('"')) ||
    (text.startsWith("'") && text.endsWith("'"))
  ) {
    return text.slice(1, -1);
  }

  return text;
}

export function HiddenContentSummary({ hiddenItems, type = "slides", onShow }) {
  const [showHidden, setShowHidden] = useState(false);

  if (!hiddenItems || hiddenItems.length === 0) return null;

  const itemLabel = type === "slides" ? "slide" : "section";
  const itemsLabel = hiddenItems.length === 1 ? itemLabel : `${itemLabel}s`;

  const handleToggle = () => {
    const newState = !showHidden;
    setShowHidden(newState);
    if (onShow) {
      onShow(newState);
    }
  };

  return (
    <div className="mt-4">
      <Alert className="border-dashed">
        <AlertDescription className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {hiddenItems.length} {itemsLabel} without data hidden
          </span>
          <Button variant="ghost" size="sm" onClick={handleToggle} className="h-auto py-1 px-2">
            {showHidden ? (
              <>
                <EyeOff className="h-3 w-3 mr-1" />
                Hide
              </>
            ) : (
              <>
                <Eye className="h-3 w-3 mr-1" />
                Show
              </>
            )}
          </Button>
        </AlertDescription>
      </Alert>

      {showHidden && (
        <div className="mt-3 space-y-3">
          {hiddenItems.map((item, index) => (
            <Card key={index} className="border-dashed opacity-60">
              <CardContent className="pt-4">
                {item.sub_request && (
                  <div className="mb-2">
                    <p className="text-xs text-muted-foreground font-medium">Intended:</p>
                    <p className="text-sm text-muted-foreground">
                      {removeWrappingQuotes(item.sub_request)}
                    </p>
                  </div>
                )}
                {item.story && (
                  <div className="text-muted-foreground prose prose-sm prose-neutral dark:prose-invert max-w-none opacity-75">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {removeWrappingQuotes(item.story)}
                    </ReactMarkdown>
                  </div>
                )}
                {item.data && (
                  <div className="mt-2 opacity-50">
                    <DataVisualization data={item.data} />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default HiddenContentSummary;
